import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CaSidebar from "./CaSidebar";
import CaHeader from "./CaHeader";
import { getAllCaFormResponses } from "../utils/api";
import { ClipLoader } from "react-spinners";

// Icons accept props now (so className can be passed)
const SearchIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const FilterIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
  </svg>
);

const EyeIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const ClockIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const UserIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

export default function TicketInterface() {
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [ticketsError, setTicketsError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" or "open"
  const [closedMode, setClosedMode] = useState(false); // toggles showing only closed tickets

  const sidebarOpen = true;
  const toggleSidebar = () => {};
  const isMobile = false;
  const fullName = "Admin";

  useEffect(() => {
    const fetchTicketsAndStartups = async () => {
      setLoadingTickets(true);
      setTicketsError(null);
      try {
        const { responses } = await getAllCaFormResponses();

        const shaped = (responses || []).map((r) => ({
          id: r.id,
          formId: r.formId,
          name: r.formName || "Untitled",
          sender: r.startup?.startupName || "Unknown",
          time: r.time || new Date().toISOString(),
          status:
            (r.status || "").charAt(0).toUpperCase() + (r.status || "").slice(1).toLowerCase(),
          priority: ["High", "Medium", "Low"][Math.floor(Math.random() * 3)],
        }));

        setTickets(shaped);
      } catch (err) {
        setTicketsError(err?.message || String(err));
      } finally {
        setLoadingTickets(false);
      }
    };

    fetchTicketsAndStartups();
  }, []);

  // Filter & sort: CLOSED tickets are excluded unless closedMode === true
  const filteredTickets = (() => {
    let list = [...tickets];

    if (closedMode) {
      // show only closed tickets when in closed mode
      list = list.filter((t) => (t.status || "").toLowerCase() === "closed");
    } else {
      // exclude closed tickets by default
      list = list.filter((t) => (t.status || "").toLowerCase() !== "closed");

      // apply statusFilter (open/all)
      if (statusFilter === "open") {
        list = list.filter((t) => (t.status || "").toLowerCase() === "open");
      }

      // apply search
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        list = list.filter((ticket) => {
          const name = (ticket.name || "").toLowerCase();
          const sender = (ticket.sender || "").toLowerCase();
          return name.includes(q) || sender.includes(q);
        });
      }
    }

    // latest first
    list.sort((a, b) => {
      const ta = new Date(a.time).getTime() || 0;
      const tb = new Date(b.time).getTime() || 0;
      return tb - ta;
    });

    return list;
  })();

  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    return (status || "").toLowerCase() === "open"
      ? `${baseClasses} bg-green-100 text-green-800`
      : `${baseClasses} bg-gray-100 text-gray-800`;
  };

  const getPriorityBadge = (priority) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    if (priority === "High") return `${baseClasses} bg-red-100 text-red-800`;
    if (priority === "Medium") return `${baseClasses} bg-yellow-100 text-yellow-800`;
    return `${baseClasses} bg-blue-100 text-blue-800`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Unknown";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <CaSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} isMobile={isMobile} />
      <div className={`flex flex-col flex-1 transition-all duration-300 ${!isMobile ? (sidebarOpen ? "ml-16 sm:ml-18 md:ml-20 lg:ml-20 xl:ml-20" : "ml-0") : "ml-0"}`}>
        <CaHeader toggleSidebar={toggleSidebar} isMobile={isMobile} fullName={fullName} />

        <div className="flex-1 overflow-auto">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 text-white">
            <div className="px-6 py-8">
              <div className="mx-auto">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div className="mb-6 lg:mb-0">
                    <h1 className="text-4xl font-bold mb-2">Support Dashboard</h1>
                    <p className="text-indigo-100 text-lg">Manage and track customer support tickets efficiently</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium">Live Updates</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="px-6 pb-8">
              <div className="mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-indigo-100 text-sm font-medium">Total Tickets</p>
                        <p className="text-3xl font-bold text-white mt-1">{tickets.length}</p>
                        <p className="text-indigo-200 text-xs mt-1">All time</p>
                      </div>
                      <div className="bg-white/20 rounded-lg p-3">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-indigo-100 text-sm font-medium">Open Tickets</p>
                        <p className="text-3xl font-bold text-white mt-1">{tickets.filter((t) => (t.status || "").toLowerCase() === "open").length}</p>
                        <p className="text-green-300 text-xs mt-1 flex items-center">
                          <span className="w-1 h-1 bg-green-300 rounded-full mr-1"></span>
                          Active
                        </p>
                      </div>
                      <div className="bg-green-500/30 rounded-lg p-3">
                        <svg className="w-6 h-6 text-green-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-indigo-100 text-sm font-medium">High Priority</p>
                        <p className="text-3xl font-bold text-white mt-1">{tickets.filter((t) => t.priority === "High").length}</p>
                        <p className="text-red-300 text-xs mt-1 flex items-center">
                          <span className="w-1 h-1 bg-red-300 rounded-full mr-1"></span>
                          Urgent
                        </p>
                      </div>
                      <div className="bg-red-500/30 rounded-lg p-3">
                        <svg className="w-6 h-6 text-red-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-indigo-100 text-sm font-medium">Resolved</p>
                        <p className="text-3xl font-bold text-white mt-1">{tickets.filter((t) => (t.status || "").toLowerCase() === "closed").length}</p>
                        <p className="text-gray-300 text-xs mt-1 flex items-center">
                          <span className="w-1 h-1 bg-gray-300 rounded-full mr-1"></span>
                          Completed
                        </p>
                      </div>
                      <div className="bg-gray-500/30 rounded-lg p-3">
                        <svg className="w-6 h-6 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="px-6 py-8">
            <div className="mx-auto">
              {/* Search and Filter Bar */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 mb-8">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <SearchIcon className="w-4 h-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        className="block w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent focus:bg-white transition-all duration-200"
                        placeholder="Search tickets by name or sender..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-3 bg-gray-50 rounded-xl px-4 py-3">
                      <FilterIcon className="w-4 h-4 text-gray-500" />
                      <select
                        className="border-0 bg-transparent focus:outline-none focus:ring-0 text-gray-700 font-medium"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        disabled={closedMode}
                      >
                        <option value="all">All Status</option>
                        <option value="open">Open</option>
                      </select>
                    </div>

                    {/* Closed Tickets toggle */}
                    <button
                      onClick={() => setClosedMode((s) => !s)}
                      className={`px-4 py-2 rounded-xl border font-medium transition ${
                        closedMode
                          ? "bg-gray-200 text-gray-900 border-gray-300"
                          : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                      }`}
                      title="Toggle closed tickets view"
                    >
                      {closedMode ? "Showing Open Tickets" : "Closed Tickets"}
                    </button>

                    <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-medium transition-colors duration-200 flex items-center space-x-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span>New Ticket</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Tickets Table */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                {loadingTickets ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="flex flex-col items-center space-y-4">
                      <ClipLoader size={18} />
                      <p className="text-gray-500 font-medium">Loading tickets...</p>
                    </div>
                  </div>
                ) : ticketsError ? (
                  <div className="p-8">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                      <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-red-800 font-medium">Error loading tickets</p>
                      <p className="text-red-600 text-sm mt-1">{ticketsError}</p>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                          <th className="px-8 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Ticket Details</th>
                          <th className="px-8 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sender</th>
                          <th className="px-8 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                          <th className="px-8 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created</th>
                          <th className="px-8 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredTickets.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="px-8 py-16 text-center">
                              <div className="flex flex-col items-center space-y-4">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                  </svg>
                                </div>
                                <div>
                                  <p className="text-xl font-semibold text-gray-900 mb-2">No tickets found</p>
                                  <p className="text-gray-500">Try adjusting your search or filter criteria</p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          filteredTickets.map((ticket) => (
                            <tr key={ticket.id} className="hover:bg-gradient-to-r hover:from-indigo-50 hover:to-blue-50 transition-all duration-200 group">
                              <td className="px-8 py-6">
                                <div className="flex items-center space-x-4">
                                  <div className="flex-shrink-0">
                                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                                      <span className="text-lg font-bold text-white">{(ticket.name || "T").charAt(0).toUpperCase()}</span>
                                    </div>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                                      {ticket.name}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">ID: #{ticket.id}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-8 py-6">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                    <UserIcon className="w-4 h-4 text-gray-600" />
                                  </div>
                                  <span className="text-sm font-medium text-gray-900">
                                    {ticket.sender}
                                  </span>
                                </div>
                              </td>
                              <td className="px-8 py-6">
                                <span className={getStatusBadge(ticket.status)}>{ticket.status}</span>
                              </td>
                              <td className="px-8 py-6">
                                <div className="flex items-center space-x-2 text-sm text-gray-600">
                                  <ClockIcon className="w-4 h-4" />
                                  <span>{formatDate(ticket.time)}</span>
                                </div>
                              </td>
                              <td className="px-8 py-6 text-right">
                                <Link
                                  to={`/tickets/${ticket.id}`}
                                  state={{ ticket }}
                                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                                >
                                  <EyeIcon className="w-4 h-4" />
                                  <span className="ml-2">View</span>
                                </Link>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
